import { useState } from "react";
import { toast } from "react-toastify";
import type { ManualEstimateReviewTracker } from "@rebuild/shared";
import { ManualEstimateReviewStage } from "@rebuild/shared";

interface EditManualEstimateReviewModalProps {
  tracker: ManualEstimateReviewTracker;
  onClose: () => void;
  onSave: (uuid: string, stage: ManualEstimateReviewStage) => Promise<void>;
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-gray-800 break-all">{value}</div>
    </div>
  );
}

function EditManualEstimateReviewModal({ tracker, onClose, onSave }: EditManualEstimateReviewModalProps) {
  const [stage, setStage] = useState<ManualEstimateReviewStage>(tracker.stage);
  const [isSaving, setIsSaving] = useState(false);

  const isDisabled = isSaving || stage === tracker.stage;

  const onSubmit = async () => {
    setIsSaving(true);
    try {
      await onSave(tracker.uuid, stage);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to update manual estimate review");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[32rem] relative">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Manual Estimate Review</h2>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <ReadOnlyField label="Original Org" value={tracker.originalOrgName || tracker.originalOrgId} />
            <ReadOnlyField label="Original Estimate ID" value={tracker.originalEstimateId} />
            <ReadOnlyField label="Estimate Type" value={tracker.originalEstimateType ?? "—"} />
            <ReadOnlyField label="Upload Source" value={tracker.uploadSource} />
            <ReadOnlyField label="Manual Estimate Review ID" value={tracker.manualEstimateReviewId} />
            <ReadOnlyField label="Created" value={new Date(tracker.createdAt).toLocaleString()} />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Stage</label>
            <select
              value={stage}
              onChange={(e) => setStage(e.target.value as ManualEstimateReviewStage)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.values(ManualEstimateReviewStage).map((stageOption) => (
                <option key={stageOption} value={stageOption}>
                  {stageOption}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={isDisabled}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditManualEstimateReviewModal;
